import React, { useState } from "react";
import {
  Grid,
  Box,
  IconButton,
  Typography,
  styled,
  Button,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { updateSearchResults, addNewMessage } from "../slice";
import { MdKeyboardArrowDown } from "react-icons/md";
import { HiOutlineTrash } from "react-icons/hi";
import { CANDIDATES_API } from "../api";
import ConfirmModal from "./ConfirmModal";
import { ReactComponent as CalendarAlt } from "../assets/icons/calendar-alt.svg";
import { ReactComponent as LocationMark } from "../assets/icons/LocationMark.svg";
import { ReactComponent as CommitmentRate } from "../assets/icons/CommitmentRate.svg";
import { ReactComponent as FileLandScapeAlt } from "../assets/icons/file-landscape-alt.svg";
import { ReactComponent as CirclesFour } from "../assets/icons/CirclesFour.svg";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  borderRadius: "20px",
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<MdKeyboardArrowDown fontSize="1.5rem" />} {...props} />
))(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

function CandidateAccordion({
  id,
  isCandidate,
  number,
  educationDegree,
  commitmentRateName,
  residenceCityName,
  birthDate = "//",
  provided,
  innerRef,
  onRemoveCandidate,
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { mutate: removeCandidateWithId } = useMutation(
    ({ id, number }) => CANDIDATES_API.RemoveCandidateWithId(id, number),
    {
      onSuccess: ({ data }) => {
        onRemoveCandidate(data);
        setOpenConfirmModal(false);
      },
      onError: () => {
        setOpenConfirmModal(false);
      },
    }
  );

  return (
    <Box
      borderRadius={3}
      my={2}
      overflow="hidden"
      sx={{ backgroundColor: "#F6F6F6" }}
      p={1}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      <ConfirmModal
        message="آیا از حذف کاندید انتخابی اطمینان دارید؟"
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onSubmit={() => removeCandidateWithId({ id, number })}
      />

      <div ref={innerRef}>
        <Accordion
          expanded={expanded}
          onChange={() => {
            setExpanded((prev) => !prev);
          }}
          sx={{ backgroundColor: "transparent" }}
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            sx={{ backgroundColor: "transparent" }}
          >
            <Grid
              container
              display="flex"
              flexDirection="row-reverse"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid display="flex" flex={1} flexDirection="row-reverse">
                <Grid
                  item
                  width="5%"
                  display={{ xs: "none", sm: "flex", md: "flex" }}
                  justifyContent="flex-end"
                  sx={{ cursor: "grab" }}
                >
                  <CirclesFour />
                </Grid>
                <Grid
                  item
                  width={isCandidate ? "24%" : "40%"}
                  display="flex"
                  justifyContent="flex-end"
                  marginRight="1.7rem"
                >
                  <Typography variant="h2" textAlign="left">
                    {number}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                display="flex"
                flex={1}
                flexDirection="row-reverse"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Grid
                  item
                  width={isCandidate ? "40%" : "54%"}
                  display={{ xs: "none", sm: "none", md: "block" }}
                >
                  <Button
                    variant="s1"
                    sx={{ fontSize: "10px" }}
                    onClick={() => navigate(`/candidate-details/${number}`)}
                  >
                    نمایش پروفایل
                  </Button>
                </Grid>

                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenConfirmModal(true);
                  }}
                >
                  <HiOutlineTrash color="#4C466F" fontSize="1.4rem" />
                </IconButton>
              </Grid>
            </Grid>
          </AccordionSummary>
          <MuiAccordionDetails sx={{ backgroundColor: "transparent" }} px={0}>
            <Grid container display="flex" flexDirection="row-reverse" justifyContent="flex-start">
              <Grid
                item
                width="50%"
                maxWidth={150}
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                flexDirection="row-reverse"
                sx={{ boxSizing: "border-box" }}
                p={1.4}
                margin={1}
                bgcolor="#E8E8E8"
                borderRadius={2}
              >
                <CalendarAlt style={{ minWidth: "1.1rem", minHeight: "1.1rem" }} />
                <Typography variant="h2" noWrap marginRight="0.5rem">
                  {birthDate}
                </Typography>
              </Grid>
              <Grid
                item
                width="50%"
                maxWidth={150}
                display="flex"
                flexDirection="row-reverse"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ boxSizing: "border-box" }}
                p={1}
                margin={1}
                bgcolor="#E8E8E8"
                borderRadius={2}
              >
                <FileLandScapeAlt />
                <Typography variant="h2" noWrap marginRight="0.5rem">
                  {educationDegree.map((degree) => degree)}
                </Typography>
              </Grid>
              <Grid
                item
                width="50%"
                maxWidth={150}
                display="flex"
                flexDirection="row-reverse"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ boxSizing: "border-box" }}
                p={1}
                margin={1}
                bgcolor="#E8E8E8"
                borderRadius={2}
              >
                <LocationMark />
                <Typography variant="h2" noWrap marginRight="0.5rem">
                  {residenceCityName}
                </Typography>
              </Grid>
              <Grid
                item
                width="50%"
                maxWidth={150}
                display="flex"
                flexDirection="row-reverse"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ boxSizing: "border-box" }}
                p={1}
                margin={1}
                bgcolor="#E8E8E8"
                borderRadius={2}
              >
                <CommitmentRate style={{ minWidth: "1.1rem", minHeight: "1.1rem" }} />
                <Typography variant="h2" noWrap marginRight="0.5rem">
                  {commitmentRateName}
                </Typography>
              </Grid>
            </Grid>
          </MuiAccordionDetails>
        </Accordion>
      </div>
    </Box>
  );
}

export default CandidateAccordion;
